<template>
  <div>
    <b-row v-if="!token" class="mb-3">
      <b-col cols="12">
        <b-button variant="primary" @click="generateToken"
          ><i class="fas fa-qrcode mr-2" />{{
            $t("actions.generate_token")
          }}</b-button
        >
        <b-button variant="default" class="ml-3" @click="ladeDaten"
          ><i class="fas fa-sync"
        /></b-button>
        <b-link
          :href="`/personalplanung/unfallsofortmeldung/form.htm?uid=${id}`"
          target="_top"
          class="btn btn-default ml-3"
          ><i class="fas fa-list mr-3" />{{ $t("actions.back") }}</b-link
        >
      </b-col>
      <b-col cols="12" class="mt-3">
        <b-alert
          show
          variant="warning"
          v-html="$t('formular.warn_readonly')"
        ></b-alert>
      </b-col>
    </b-row>

    <bx-box variant="gray" header-class="col-6" class="mb-3">
      <template #header>
        <h3>{{ formular_text("headline") }}</h3>
      </template>
      <bx-box variant="white" body-class="p-3">
        <b-skeleton-wrapper :loading="loading">
          <template #loading>
            <b-skeleton-img height="350px"></b-skeleton-img>
          </template>
          <b-row>
            <b-col md="4">
              <b-form-group :label="formular_text('date')">
                <b-input type="date" v-model="accident_date" />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group :label="formular_text('time')">
                <b-input type="time" v-model="accident_time" />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group :label="formular_text('line')">
                <b-input type="text" v-model="line" />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group :label="formular_text('reg_nr')">
                <b-input type="text" v-model="reg_nr" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group :label="formular_text('from_stop')">
                <b-input type="text" v-model="from_stop" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group :label="formular_text('to_stop')">
                <b-input type="text" v-model="to_stop" />
              </b-form-group>
            </b-col>
          </b-row>
        </b-skeleton-wrapper>
      </bx-box>
    </bx-box>

    <bx-box variant="gray" header-class="col-6" class="mb-3">
      <template #header>
        <h4>{{ formular_text("accident_site.headline") }}</h4>
      </template>
      <bx-box variant="white" body-class="p-3">
        <b-skeleton-wrapper :loading="loading">
          <template #loading>
            <b-skeleton-img height="350px"></b-skeleton-img>
          </template>
          <b-row>
            <b-col md="6">
              <b-form-group
                :label="formular_text('accident_site.location')"
                :invalid-feedback="
                  formular_text('accident_site.location_invalid')
                "
                :state="input_state('location')"
              >
                <b-input
                  type="text"
                  v-model="location"
                  :state="input_state('location')"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="formular_text('accident_site.street_number')"
                :state="input_state('street')"
              >
                <b-input
                  type="text"
                  v-model="street"
                  :state="input_state('street')"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group :label="formular_text('accident_site.side_street')">
                <b-input type="text" v-model="side_street" />
              </b-form-group>
            </b-col>
          </b-row>
        </b-skeleton-wrapper>
      </bx-box>
    </bx-box>

    <bx-box variant="gray" class="mb-3">
      <bx-box variant="white" body-class="p-3">
        <b-skeleton-wrapper :loading="loading">
          <template #loading>
            <b-skeleton-img height="350px"></b-skeleton-img>
          </template>
          <b-row>
            <b-col md="3">
              <b-form-group
                :label="formular_text('road_condition')"
                v-slot="{ ariaDescribedby }"
                :invalid-feedback="formular_text('road_condition_invalid')"
                :state="input_state('road_condition')"
              >
                <b-form-checkbox-group
                  id="road_condition"
                  v-model="road_condition"
                  :options="road_condition_list"
                  :aria-describedby="ariaDescribedby"
                  :state="input_state('road_condition')"
                  size="lg"
                  stacked
                  switches
                ></b-form-checkbox-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group
                :label="formular_text('weather')"
                v-slot="{ ariaDescribedby }"
                :invalid-feedback="formular_text('weather_invalid')"
                :state="input_state('weather')"
              >
                <b-form-checkbox-group
                  id="weather"
                  v-model="weather"
                  :options="weather_list"
                  :aria-describedby="ariaDescribedby"
                  :state="input_state('weather')"
                  size="lg"
                  stacked
                  switches
                ></b-form-checkbox-group>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="formular_text('involved_vehicles')"
                v-slot="{ ariaDescribedby }"
              >
                <b-form-checkbox-group
                  id="involved_vehicles"
                  v-model="involved_vehicles"
                  :options="involved_vehicles_list"
                  :aria-describedby="ariaDescribedby"
                  size="lg"
                  stacked
                  switches
                ></b-form-checkbox-group>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="formular_text('driver')"
                :state="input_state('driver_id')"
                :invalid-feedback="formular_text('driver_invalid')"
              >
                <v-select
                  :options="employee_list"
                  :reduce="(o) => o.id"
                  v-model="driver_id"
                  label="id"
                  :class="{ 'is-invalid': input_state('driver_id') === false }"
                  :get-option-label="(o) => `${o.lastname}, ${o.firstname}`"
                ></v-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                :label="formular_text('accident_vehicle')"
                :state="input_state('accident_vehicle_id')"
                :invalid-feedback="formular_text('accident_vehicle_invalid')"
              >
                <v-select
                  v-model="accident_vehicle_id"
                  :options="vehicle_list"
                  :reduce="(o) => o.id"
                  :class="{
                    'is-invalid': input_state('accident_vehicle_id') === false,
                  }"
                  label="plate"
                ></v-select>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group :label="formular_text('pictures')">
                <b-form-file
                  id="file-input"
                  v-model="addedFiles"
                  :placeholder="formular_text('files_upload_placeholder')"
                  :browse-text="$t('actions.search')"
                  multiple
                  ref="fileInput"
                  class="mb-2"
                ></b-form-file>
              </b-form-group>
            </b-col>
            <b-col v-for="a in accident_media" :key="a.key" cols="4">
              <img :src="a.data.base64" class="w-100" />
            </b-col>
          </b-row>
        </b-skeleton-wrapper>
      </bx-box>
    </bx-box>

    <bx-box variant="gray" class="mb-3" header-class="col-md-6">
      <template #header>
        <h4 class="headline">
          {{ formular_text("accident_involved.headline") }}
        </h4>
      </template>
      <bx-box variant="white" body-class="p-3">
        <b-skeleton-wrapper :loading="loading">
          <template #loading>
            <b-skeleton-img height="350px"></b-skeleton-img>
          </template>
          <b-row>
            <b-col
              v-for="(it, index) in involved_type_list"
              :key="it.value"
              cols="12"
              :class="{ 'mb-5': index < involved_type_list.length - 1 }"
            >
              <h6>{{ it.text }}</h6>
              <hr />
              <transition-group name="fade">
                <accident-involved
                  v-for="a in accident_involved.filter(
                    (e) => e.type == it.value && !e.delete
                  )"
                  :key="a.key"
                  v-model="a.data"
                  @delete="a.delete = true"
                />
              </transition-group>
              <b-button
                variant="primary"
                @click="addAccidentInvolved(it.value)"
                >{{ $t("actions.add") }}</b-button
              >
            </b-col>
          </b-row>
        </b-skeleton-wrapper>
      </bx-box>
    </bx-box>

    <bx-box variant="gray" class="mb-3" header-class="col-md-6">
      <template #header>
        <h4 class="headline">{{ formular_text("damage.headline") }}</h4>
      </template>
      <bx-box variant="white" body-class="p-3">
        <b-skeleton-wrapper :loading="loading">
          <template #loading>
            <b-skeleton-img height="350px"></b-skeleton-img>
          </template>
          <b-row>
            <b-col md="12">
              <b-form-group
                :label="formular_text('damage.damage_statement')"
                :invalid-feedback="
                  formular_text('damage.damage_statement_invalid')
                "
                :state="input_state('damage_statement')"
                v-slot="{ ariaDescribedby }"
              >
                <b-form-checkbox-group
                  id="damage_statement"
                  v-model="damage_statement"
                  :state="input_state('damage_statement')"
                  :options="damage_list"
                  :aria-describedby="ariaDescribedby"
                  size="lg"
                  switches
                ></b-form-checkbox-group>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group :label="formular_text('damage.third_party_damage')">
                <b-textarea v-model="third_party_damage" rows="3" />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group :label="formular_text('damage.old_damage')">
                <b-textarea v-model="old_damage" rows="3" />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group :label="formular_text('damage.own_damage')">
                <b-textarea v-model="own_damage" rows="3" />
              </b-form-group>
            </b-col>
          </b-row>
        </b-skeleton-wrapper>
      </bx-box>
    </bx-box>

    <bx-box variant="gray" class="mb-3" header-class="col-md-6">
      <template #header>
        <h4 class="headline">
          {{ formular_text("more_informations.headline") }}
        </h4>
      </template>
      <bx-box variant="white" body-class="p-3">
        <b-skeleton-wrapper :loading="loading">
          <template #loading>
            <b-skeleton-img height="350px"></b-skeleton-img>
          </template>
          <b-row>
            <b-col md="3">
              <b-form-group
                :label="formular_text('more_informations.bus_type')"
                v-slot="{ ariaDescribedby }"
              >
                <b-form-radio-group
                  id="bustypes"
                  v-model="bus_type"
                  :options="bus_type_list"
                  :aria-describedby="ariaDescribedby"
                  size="lg"
                  stacked
                ></b-form-radio-group>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group
                :label="
                  formular_text('more_informations.advertisement_damaged')
                "
                v-slot="{ ariaDescribedby }"
              >
                <b-form-radio
                  v-model="advertisement_damaged"
                  :aria-describedby="ariaDescribedby"
                  name="advertisement_damaged"
                  size="lg"
                  :value="true"
                >
                  {{ $t("common.yes") }}
                </b-form-radio>
                <b-form-radio
                  v-model="advertisement_damaged"
                  :aria-describedby="ariaDescribedby"
                  name="advertisement_damaged"
                  size="lg"
                  :value="false"
                >
                  {{ $t("common.no") }}
                </b-form-radio>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group
                :label="formular_text('more_informations.foreman_present')"
                v-slot="{ ariaDescribedby }"
              >
                <b-form-radio
                  v-model="foreman_present"
                  :aria-describedby="ariaDescribedby"
                  name="foreman_present"
                  size="lg"
                  :value="true"
                >
                  {{ $t("common.yes") }}
                </b-form-radio>
                <b-form-radio
                  v-model="foreman_present"
                  :aria-describedby="ariaDescribedby"
                  name="foreman_present"
                  size="lg"
                  :value="false"
                >
                  {{ $t("common.no") }}
                </b-form-radio>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group
                :label="formular_text('more_informations.police_record')"
                v-slot="{ ariaDescribedby }"
              >
                <b-form-radio
                  v-model="police_record"
                  :aria-describedby="ariaDescribedby"
                  name="police_record"
                  size="lg"
                  :value="true"
                >
                  {{ $t("common.yes") }}
                </b-form-radio>
                <b-form-radio
                  v-model="police_record"
                  :aria-describedby="ariaDescribedby"
                  name="police_record"
                  size="lg"
                  :value="false"
                >
                  {{ $t("common.no") }}
                </b-form-radio>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group
                :label="
                  formular_text(
                    'more_informations.acknowledgment_of_guilt.label'
                  )
                "
                v-slot="{ ariaDescribedby }"
              >
                <b-form-radio
                  v-model="acknowledgment_of_guilt"
                  :aria-describedby="ariaDescribedby"
                  name="acknowledgment_of_guilt"
                  size="lg"
                  :value="null"
                >
                  {{
                    formular_text(
                      "more_informations.acknowledgment_of_guilt.no_one"
                    )
                  }}
                </b-form-radio>
                <b-form-radio
                  v-model="acknowledgment_of_guilt"
                  :aria-describedby="ariaDescribedby"
                  name="acknowledgment_of_guilt"
                  size="lg"
                  :value="false"
                >
                  {{
                    formular_text(
                      "more_informations.acknowledgment_of_guilt.the_other"
                    )
                  }}
                </b-form-radio>
                <b-form-radio
                  v-model="acknowledgment_of_guilt"
                  :aria-describedby="ariaDescribedby"
                  name="acknowledgment_of_guilt"
                  size="lg"
                  :value="true"
                >
                  {{
                    formular_text(
                      "more_informations.acknowledgment_of_guilt.self"
                    )
                  }}
                </b-form-radio>
              </b-form-group>
            </b-col>
            <b-col md="5">
              <b-form-group
                :label="formular_text('more_informations.file_number')"
              >
                <b-form-input type="text" v-model="file_number" />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                :label="formular_text('more_informations.cause_of_accident')"
              >
                <b-form-textarea type="text" v-model="cause_of_accident" />
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group
                :label="formular_text('more_informations.the_accident')"
                :invalid-feedback="
                  formular_text('more_informations.the_accident_invalid')
                "
                :state="input_state('the_accident')"
              >
                <b-form-textarea
                  rows="5"
                  type="text"
                  v-model="the_accident"
                  :state="input_state('the_accident')"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-skeleton-wrapper>
      </bx-box>
    </bx-box>

    <bx-box variant="gray" class="mb-3" header-class="col-md-6">
      <template #header>
        <h4 class="headline">
          {{ formular_text("warnings.headline") }}
        </h4>
      </template>
      <bx-box variant="white" body-class="p-3">
        <b-skeleton-wrapper :loading="loading">
          <template #loading>
            <b-skeleton-img height="350px"></b-skeleton-img>
          </template>
          <b-row>
            <b-col md="3">
              <b-form-group
                :label="formular_text('warnings.cautioned.label')"
                v-slot="{ ariaDescribedby }"
              >
                <b-form-radio
                  v-model="cautioned"
                  :aria-describedby="ariaDescribedby"
                  name="cautioned"
                  size="lg"
                  :value="null"
                >
                  {{ formular_text("warnings.cautioned.no_one") }}
                </b-form-radio>
                <b-form-radio
                  v-model="cautioned"
                  :aria-describedby="ariaDescribedby"
                  name="cautioned"
                  size="lg"
                  :value="false"
                >
                  {{ formular_text("warnings.cautioned.the_other") }}
                </b-form-radio>
                <b-form-radio
                  v-model="cautioned"
                  :aria-describedby="ariaDescribedby"
                  name="cautioned"
                  size="lg"
                  :value="true"
                >
                  {{ formular_text("warnings.cautioned.self") }}
                </b-form-radio>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group
                :label="formular_text('warnings.police')"
                v-slot="{ ariaDescribedby }"
              >
                <b-form-radio
                  v-model="warning_police"
                  :aria-describedby="ariaDescribedby"
                  name="warning_police"
                  size="lg"
                  :value="true"
                >
                  {{ $t("common.yes") }}
                </b-form-radio>
                <b-form-radio
                  v-model="warning_police"
                  :aria-describedby="ariaDescribedby"
                  name="warning_police"
                  size="lg"
                  :value="false"
                >
                  {{ $t("common.no") }}
                </b-form-radio>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group
                :label="formular_text('warnings.oral')"
                v-slot="{ ariaDescribedby }"
              >
                <b-form-radio
                  v-model="warning_oral"
                  :aria-describedby="ariaDescribedby"
                  name="warning_oral"
                  size="lg"
                  :value="true"
                >
                  {{ $t("common.yes") }}
                </b-form-radio>
                <b-form-radio
                  v-model="warning_oral"
                  :aria-describedby="ariaDescribedby"
                  name="warning_oral"
                  size="lg"
                  :value="false"
                >
                  {{ $t("common.no") }}
                </b-form-radio>
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group
                :label="formular_text('warnings.chargeable')"
                v-slot="{ ariaDescribedby }"
              >
                <b-form-radio
                  v-model="warning_chargeable"
                  :aria-describedby="ariaDescribedby"
                  name="warning_chargeable"
                  size="lg"
                  :value="true"
                >
                  {{ $t("common.yes") }}
                </b-form-radio>
                <b-form-radio
                  v-model="warning_chargeable"
                  :aria-describedby="ariaDescribedby"
                  name="warning_chargeable"
                  size="lg"
                  :value="false"
                >
                  {{ $t("common.no") }}
                </b-form-radio>
              </b-form-group>
            </b-col>
          </b-row>
        </b-skeleton-wrapper>
      </bx-box>
    </bx-box>

    <bx-box variant="gray" class="mb-3" header-class="col-md-5">
      <template #header>
        <h4 class="headline">
          {{ formular_text("drawing.headline") }}
        </h4>
      </template>
      <bx-box variant="white" class="mb-1" body-class="p-3">
        <b-skeleton-wrapper :loading="loading">
          <template #loading>
            <b-skeleton-img height="350px"></b-skeleton-img>
          </template>
          <b-row>
            <b-col v-if="!accident_drawing" cols="12">
              <h6>{{ formular_text("drawing.no_drawing") }}</h6>
              <b-button variant="primary" @click="$refs.editor.show()">{{
                formular_text("drawing.create_drawing")
              }}</b-button>
            </b-col>
            <b-col class="text-center" v-else cols="12">
              <img
                class="w-100"
                :src="accident_drawing"
                @click="$refs.editor.show()"
              />

              <b-button class="mt-5" variant="primary" @click="changeDrawing">{{
                formular_text("drawing.change_drawing")
              }}</b-button>
            </b-col>
            <editor
              :canvasWidth="accident_drawing_width"
              :canvasHeight="accident_drawing_height"
              ref="editor"
              :background-image="
                accident_drawing || '/digital-formulars/img/unfallskizze.png'
              "
              @image-saved="(i) => (accident_drawing = i)"
            />
          </b-row>
        </b-skeleton-wrapper>
      </bx-box>
    </bx-box>
    <bx-box variant="gray" class="mb-3">
      <bx-box variant="white" class="mb-1" body-class="p-3">
        <b-skeleton-wrapper :loading="loading">
          <template #loading>
            <b-skeleton-img height="350px"></b-skeleton-img>
          </template>
          <b-row>
            <b-col md="6">
              <h6>{{ formular_text("signature.reporter") }}</h6>
              <signature-pad
                ref="signaturePadReporter"
                height="200px"
                v-model="signature_reporter"
                :disabled="signature_reporter !== null"
                :state="input_state('signature_reporter')"
                :invalid-feedback="formular_text('signature.reporter_invalid')"
              ></signature-pad>
            </b-col>
            <b-col md="6">
              <h6>{{ formular_text("signature.foreman") }}</h6>
              <signature-pad
                ref="signaturePadForeman"
                height="200px"
                v-model="signature_foreman"
                :state="input_state('signature_foreman')"
                :disabled="signature_foreman !== null"
                :invalid-feedback="formular_text('signature.foreman_invalid')"
              ></signature-pad>
            </b-col>
          </b-row>
        </b-skeleton-wrapper>
      </bx-box>
    </bx-box>
    <b-row>
      <b-col cols="6">
        <b-button
          v-if="!id"
          variant="default"
          class="w-100"
          @click="speichern(false)"
        >
          {{ $t("actions.cache") }}
        </b-button>
      </b-col>
      <b-col cols="6">
        <b-button
          v-if="!id"
          variant="primary"
          class="w-100"
          :disabled="errors_activated && $v.$invalid"
          @click="speichern(true)"
        >
          {{ formular_text("report_damage") }}
        </b-button>
      </b-col>
    </b-row>

    <token-invalid-modal ref="tokenInvalidModal"></token-invalid-modal>
    <speichern-erfolgreich-modal
      ref="speichernErfolgreichModal"
    ></speichern-erfolgreich-modal>
    <unexpected-error-modal ref="unexpectedErrorModal"></unexpected-error-modal>
    <validation-failure-modal
      ref="validationFailureModal"
    ></validation-failure-modal>
  </div>
</template>

<script>
  import formular from "@m/formular";
  import api from "@a/index";
  import { DateTime } from "luxon";
  import EventBus from "@/event-bus.js";
  import { OPEN_ACCESS_LAYER } from "@/event-bus";
  import { formularKeys } from "@/services/formulars/formularKeyDecoder";
  import AccidentInvolved from "@c/Formulars/Employee/Accidentreport/Accidentinvolved.vue";
  import Editor from "@/components/DrawingEditor.vue";
  import { v4 as uuid } from "uuid";
  import SignaturePad from "@c/SignaturePad.vue";

  import { required } from "vuelidate/lib/validators";

  import { getBase64 } from "@u/file";

  const accident_drawing_ratio = 1.72;

  export default {
    name: "Accidentreport",
    metaInfo() {
      const title = this.formular_text("headline");
      return {
        titleTemplate: () => {
          return title;
        },
      };
    },
    components: {
      AccidentInvolved,
      Editor,
      SignaturePad,
    },
    mixins: [formular],
    data() {
      return {
        accident_date: null,
        accident_drawing: false,
        accident_drawing_height: 0,
        accident_drawing_width: 0,
        accident_involved: [],
        accident_media: [],
        accident_time: null,
        accident_vehicle_id: null,
        accident_vehicle: null,
        acknowledgment_of_guilt: null,
        advertisement_damaged: false,
        bus_type: null,
        bus_type_list: [],
        cause_of_accident: null,
        cautioned: null,
        damage_list: [],
        damage_statement: [],
        driver: null,
        driver_id: null,
        employee_list: [],
        file_number: null,
        foreman_present: false,
        from_stop: null,
        involved_type_list: [],
        involved_vehicles: [],
        involved_vehicles_list: [],
        drawing_key: uuid(),
        line: null,
        loading: true,
        location: null,
        old_damage: null,
        own_damage: null,
        police_record: false,
        reg_nr: null,
        road_condition: [],
        road_condition_list: [],
        side_street: null,
        street: null,
        signature_foreman: null,
        signature_reporter: null,
        the_accident: null,
        third_party_damage: null,
        to_stop: null,
        vehicle_list: [],
        warning_chargeable: false,
        warning_oral: false,
        warning_police: false,
        weather: [],
        weather_list: [],
        report_id: null,
        reporter_id: null,
        errors_activated: false,
        addedFilesBase64: [],
        addedFiles: [],
      };
    },
    validations: {
      location: {
        required,
      },
      road_condition: {
        required,
      },
      weather: {
        required,
      },
      driver_id: {
        required,
      },
      accident_vehicle_id: {
        required,
      },
      damage_statement: {
        required,
      },
      the_accident: {
        required,
      },
    },
    computed: {
      formular_key() {
        return "employee.accidentreport";
      },
    },
    watch: {
      addedFiles: {
        handler: function (val) {
          if (val && val.length > 0) {
            let files = document.getElementById("file-input").files;
            var self = this;
            files.forEach(async (f) => {
              self.addedFilesBase64.push(await getBase64(f));
            });
          }
        },
      },
    },
    async created() {
      await this.ladeDaten();
      this.loading = false;
    },
    mounted() {
      this.accident_drawing_height = window.innerHeight - 250;
      this.accident_drawing_width =
        this.accident_drawing_height * accident_drawing_ratio;
    },
    methods: {
      async ladeDaten() {
        let loader = this.$loading.show({});
        try {
          let response = this.id
            ? await api.privateFormular.employee.accidentreport.getById(this.id)
            : this.token
            ? await api.publicFormluar.employee.accidentreport.get(this.token)
            : null;

          this.initialize(response?.data);
        } catch (e) {
          console.error(e);
          if (e.response.status === 401) {
            this.$refs.tokenInvalidModal.show();
          }
        } finally {
          this.loading = false;
          loader.hide();
        }
      },
      async speichern(abschliessen = false) {
        if (this.$v.$invalid && abschliessen) {
          this.errors_activated = true;
          this.$refs.validationFailureModal.show();
        } else {
          let loader = this.$loading.show({});

          try {
            let json = this.toJson();
            await api.publicFormluar.employee.accidentreport.post(
              json,
              this.token,
              abschliessen
            );

            if (abschliessen) this.$refs.speichernErfolgreichModal.show();
            else await this.ladeDaten();
          } catch (e) {
            console.error(e);
            if (e.response?.status === 401) {
              this.$refs.tokenInvalidModal.show();
            } else {
              this.$refs.unexpectedErrorModal.show();
            }
          } finally {
            loader.hide();
          }
        }
      },
      // eslint-disable-next-line no-unused-vars

      initializeCategories(categories) {
        this.road_condition_list = categories.road_conditions.map((e) => {
          return {
            value: e.id,
            text: e.title,
          };
        });
        this.weather_list = categories.weathers.map((e) => {
          return {
            value: e.id,
            text: e.title,
          };
        });
        this.involved_vehicles_list = categories.involved_vehicles.map((e) => {
          return {
            value: e.id,
            text: e.title,
          };
        });
        this.involved_type_list = categories.involved_types.map((e) => {
          return {
            value: e.id,
            text: e.title,
          };
        });
        this.damage_list = categories.accident_damages.map((e) => {
          return {
            value: e.id,
            text: e.title,
          };
        });
        this.bus_type_list = categories.bus_types.map((e) => {
          return {
            value: e.id,
            text: e.title,
          };
        });

        this.employee_list = categories.employees;
        this.vehicle_list = categories.vehicles;
      },
      initializeReport(report) {
        this.accident_date = report.accident_time
          ? DateTime.fromMillis(report.accident_time).toFormat("yyyy-MM-dd")
          : null;
        this.accident_time = report.accident_time
          ? DateTime.fromMillis(report.accident_time).toFormat("HH:mm")
          : null;

        this.report_id = report.id;
        this.reporter_id = report.reporter_id;
        this.line = report.line;
        this.reg_nr = report.reg_nr;
        this.from_stop = report.from_stop;
        this.to_stop = report.to_stop;
        this.accident_drawing = report.accident_drawing;
        this.accident_vehicle_id = report.accident_vehicle_id;
        this.acknowledgment_of_guilt = report.acknowledgment_of_guilt;
        this.advertisement_damaged = report.advertisement_damaged;
        this.bus_type = report.bus_Type_id;
        this.cause_of_accident = report.cause_of_accident;
        this.cautioned = report.cautioned;
        this.damage_statement = report.damage_statement;
        this.driver_id = report.driver_id;
        this.file_number = report.file_number;
        this.foreman_present = report.foreman_present;
        this.involved_vehicles = report.involved_vehicles;
        this.location = report.location;
        this.old_damage = report.old_damage;
        this.own_damage = report.own_damage;
        this.police_record = report.police_record;
        this.road_condition = report.road_condition;
        this.weather = report.weather;
        this.side_street = report.side_street;
        this.signature_reporter = report.signature_reporter;
        this.signature_foreman = report.signature_foreman;
        this.the_accident = report.the_accident;

        this.addedFilesBase64 = [];
        this.addedFiles = [];

        this.accident_involved = report.accident_involved.map((ai) => {
          return {
            data: ai,
            key: uuid(),
            type: ai.type_id,
            delete: false,
          };
        });

        this.accident_media = report.accident_media.map((am) => {
          return {
            data: am,
            key: uuid(),
            delete: false,
          };
        });
      },
      toJson() {
        return {
          id: this.report_id,
          accident_time: this.accident_time
            ? DateTime.fromFormat(
                `${this.accident_date} ${this.accident_time}`,
                "yyyy-MM-dd HH:mm"
              ).ts
            : null,
          reg_nr: this.reg_nr,
          reporter_id: this.reporter_id,
          accident_involved: this.accident_involved.map((e) => {
            let d = e.data;
            d.type_id = e.type;
            d.delete = e.delete;
            return d;
          }),
          accident_media: this.addedFilesBase64.map((e) => {
            return {
              base64: e,
            };
          }),
          accident_vehicle_id: this.accident_vehicle_id,
          acknowledgment_of_guilt: this.acknowledgment_of_guilt,
          accident_drawing: this.accident_drawing,
          advertisement_damaged: this.advertisement_damaged,
          bus_Type_id: this.bus_Type_id,
          cause_of_accident: this.cause_of_accident,
          cautioned: this.cautioned,
          damage_statement: this.damage_statement,
          driver_id: this.driver_id,
          file_number: this.file_number,
          foreman_present: this.foreman_present,
          from_stop: this.from_stop,
          involved_vehicles: this.involved_vehicles,
          line: this.line,
          location: this.location,
          old_damage: this.old_damage,
          own_damage: this.own_damage,
          police_record: this.police_record,
          road_condition: this.road_condition,
          side_street: this.side_street,
          signature_foreman: this.signature_foreman,
          signature_reporter: this.signature_reporter,
          street: this.street,
          the_accident: this.the_accident,
          third_party_damage: this.third_party_damage,
          to_stop: this.to_stop,
          warning_chargeable: this.warning_chargeable,
          warning_oral: this.warning_oral,
          warning_police: this.warning_police,
          weather: this.weather,
        };
      },
      initialize(responseData) {
        if (responseData) {
          this.initializeCategories(responseData.categories);
          if (responseData.data) {
            this.initializeReport(responseData.data);
          }
        }
      },
      saveDrawing() {
        this.accident_drawing = this.$refs.editor.getImage();
        this.$refs.accidentModal.hide();
      },
      addAccidentInvolved(type) {
        this.accident_involved.push({
          delete: false,
          key: uuid(),
          type: type,
        });
      },
      changeDrawing() {
        this.drawing_key = uuid();
        this.$refs.editor.show();
      },
      deleteDrwaing() {
        this.accident_drawing = null;
        this.drawing_key = uuid();
      },
      generateToken() {
        EventBus.$emit(
          OPEN_ACCESS_LAYER,
          formularKeys.accidentreport,
          "Unfallmeldung",
          DateTime.local().plus({ days: 1 }),
          this.id
        );
      },
    },
  };
</script>

<style>
</style>
